import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, FormControl, TextField, Button, Grid } from '@mui/material';
import axios from 'axios';
import ModalPrice from './ModalPrice';
import { setTemplatesInBicUtility } from '../helpers';
import { actions } from '../../../actions';
import collections from '../../../constants/FirestoreCollections';
import { useFirestore } from 'react-redux-firebase';

function PriceForm() {
  const [prices, setPrices] = useState({});
  const [inflation, setInflation] = useState(0);
  const [reloadData, setReloadData] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isV2, setIsV2] = useState(false);

  const organization = useSelector((state) => state.app.organization);
  const profile = useSelector((state) => state.firebase.profile);
  const prestobotsConfig = useSelector((state) => state.app.prestobotsConfig);
  const appDispatch = useDispatch();
  const firestore = useFirestore();

  // methods
  const setChargingModal = (boolean) => {
    return appDispatch({
      type: actions.changeLoading,
      loading: { loading: boolean, priority: 1 },
    });
  };
  const setSnackBarAlert = (message, severity) => {
    return appDispatch({
      type: actions.alert,
      alert: {
        snackbarOpen: true,
        snackbarMessage: message,
        snackbarSeverity: severity,
      },
    });
  };

  useEffect(() => {
    getPriceData();
  }, []);

  useEffect(() => {
    getPriceData();
  }, [reloadData, prestobotsConfig?.inflationBilling]);

  useEffect(() => {
    setTemplates(organization.templates);
  }, [organization]);

  useEffect(() => {
    setChargingModal(true);
    if (prices?.priceBICUtility?.utility) {
      setTemplatesInBicUtility(prices, templates);
    }
    setChargingModal(false);
  }, [prices, templates]);

  const getPriceData = () => {
    setChargingModal(true);
    // eslint-disable-next-line no-undef
    const localStoragedPrices = JSON.parse(localStorage.getItem('prices'));
    // eslint-disable-next-line no-undef
    setIsV2(localStorage.getItem('isV2'));

    if (localStoragedPrices) {
      setPrices(localStoragedPrices);
    }

    prestobotsConfig?.inflationBilling && setInflation(prestobotsConfig.inflationBilling);
    setChargingModal(false);
  };

  const checkStringsFromObject = () => {
    const updatedPrices = { ...prices };
    for (const key in updatedPrices) {
      if (typeof updatedPrices[key] === 'string') {
        updatedPrices[key] = parseFloat(updatedPrices[key]);
      }
      if (updatedPrices[key] === null) {
        updatedPrices[key] = 0;
      }
    }
    return updatedPrices;
  };

  const multiplyPrices = (prices, inflation) => {
    try {
      for (const key in prices) {
        if (typeof prices[key] === 'object' && prices[key] !== null) {
          multiplyPrices(prices[key], inflation);
        } else if (typeof prices[key] === 'number') {
          prices[key] *= inflation;
        }
      }
      return prices;
    } catch (error) {
      console.log('multiplyPrices', error);
      setSnackBarAlert('Ocurrio un error, intente mas tarde', 'error');
    }
  };

  const handleInputChange = (e, key) => {
    let input = parseFloat(e.target.value);
    if (!input) input = 0;
    setPrices({ ...prices, [key]: input });
  };

  const handleSubmit = async () => {
    setChargingModal(true);
    const objectToSend = checkStringsFromObject();
    // eslint-disable-next-line no-undef
    const date = JSON.parse(localStorage.getItem('date'));

    axios(`${process.env.REACT_APP_BILLING_URL}/update-prices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        organizationId: organization.id,
        date: date,
        prices: objectToSend,
        isV2: isV2,
        requestedBy: profile.email,
      },
    })
      .then((response) => {
        // eslint-disable-next-line no-undef
        localStorage.setItem('prices', JSON.stringify(objectToSend));
        setPrices(objectToSend);
        setSnackBarAlert('¡Precios actualizados exitosamente!', 'success');
      })
      .catch((err) => {
        console.log('handleSubmit', err);
        setSnackBarAlert('Ocurrio un error, intente mas tarde', 'error');
        setChargingModal(false);
      });
  };
  const handlePrices = async () => {
    setChargingModal(true);
    // eslint-disable-next-line no-undef
    const date = JSON.parse(localStorage.getItem('date'));

    axios(`${process.env.REACT_APP_BILLING_URL}/get-old-prices`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      params: {
        organizationId: organization.id,
        date: date,
      },
    })
      .then(({ data }) => {
        setPrices(data);
        setSnackBarAlert('¡Precios obtenidos exitosamente!', 'success');
      })
      .catch((err) => {
        console.log('handlePrices', err);
        setSnackBarAlert('Ocurrio un error, intente mas tarde', 'error');
        setChargingModal(false);
      });
  };

  const handleUpdateInflationBillings = async (data) => {
    try {
      const docId = prestobotsConfig.id;
      await firestore
        .collection(collections.configurations)
        .doc(docId)
        .update({ inflationBilling: inflation });
      setSnackBarAlert('Inflacion actualizada exitosamente!', 'success');
    } catch (error) {
      console.log('handleUpdateInflationBillings', error);
      setSnackBarAlert('Ocurrio un error, intente mas tarde', 'error');
    }
  };

  const handleInflation = async () => {
    setChargingModal(true);
    const objetoMultiplicado = multiplyPrices({ ...prices }, inflation / 100 + 1);
    setSnackBarAlert('Inflacion aplicada exitosamente!', 'success');
    setPrices(objetoMultiplicado);
  };

  const handleOpenModal = (infoBIC) => {
    setModalOpen(true);
  };

  const updateBicInParentComponent = (updatedObjectBic) => {
    setPrices({ ...prices, priceBICUtility: updatedObjectBic });
  };
  return (
    <Grid container direction="column" justifyContent="center" alignItems="center" width="100%">
      <Box sx={{ width: '100%', display: 'flex', mb: '20px' }}>
        <Box sx={{ width: '50%', display: 'flex', mb: '10px', justifyContent: 'space-between' }}>
          <TextField
            label="Porcentaje inflacion"
            value={inflation}
            onChange={(event) => setInflation(event.target.value)}
            type="number"
            style={{ width: '30%' }}
          />
          <Button variant="contained" color="primary" onClick={handleInflation}>
            Aplicar
          </Button>
          <Button variant="contained" color="primary" onClick={handleUpdateInflationBillings}>
            Actualizar Inflacion
          </Button>
        </Box>
        <Box sx={{ width: '50%', display: 'flex', mb: '10px', justifyContent: 'space-evenly' }}>
          <Button variant="contained" color="primary" onClick={handlePrices}>
            Obtener Precios Mes Previo
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2}>
        {Object.keys(prices)
          .sort()
          .map((key) => (
            <Grid item xs={6} key={key}>
              <FormControl fullWidth>
                {isV2 && key === 'priceBICUtility' ? (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ fontSize: '0.8rem', padding: '16px 32px' }}
                    disabled={prices?.priceBICUtility?.utility?.length <= 0}
                    onClick={() => {
                      handleOpenModal();
                    }}
                  >
                    Precios BIC Utility
                  </Button>
                ) : (
                  <TextField
                    label={`Valor ${key}`}
                    value={
                      prices[key] === 0 || Number.isInteger(prices[key])
                        ? prices[key]
                        : prices[key]
                        ? prices[key].toFixed(2)
                        : 0
                    }
                    onChange={(event) => handleInputChange(event, key)}
                    type="number"
                  />
                )}
              </FormControl>
            </Grid>
          ))}
      </Grid>
      <Box sx={{ width: '100%', display: 'flex', mt: '20px', justifyContent: 'space-evenly' }}>
        <Button variant="contained" color="primary" onClick={() => handleSubmit()}>
          Actualizar
        </Button>
        <Button variant="contained" color="primary" onClick={() => setReloadData(!reloadData)}>
          Cancelar
        </Button>
      </Box>
      <ModalPrice
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        templates={organization.templates}
        updateBicInParentComponent={updateBicInParentComponent}
        priceBICUtility={prices.priceBICUtility}
      />
    </Grid>
  );
}

export default PriceForm;
